/**
 * Types for the admin-editable marketing content.
 *
 *   GET /api/site-content/  ->  every public-page section in one response
 *
 * Content is authored in the Django admin (Site content → …), so the pages
 * reflect admin changes with no deploy.
 *
 * Every field is optional by design. A blank value means "no admin override",
 * and each component keeps its shipped copy as a fallback — see `pick()`.
 * A missing section, a blanked field and an unreachable API all degrade to
 * the same thing: the site exactly as it was before it became editable.
 */

export interface HeroCardContent {
  id: number;
  title: string;
  description: string;
  link: string;
  icon: string;
  image_url: string | null;
  order: number;
}

export interface HeroContent {
  headline: string;
  intro: string;
  background_image_url: string | null;
  cards: HeroCardContent[];
}

export interface AboutContent {
  quote: string;
  attribution_name: string;
  attribution_title: string;
  portrait_url: string | null;
  heading: string;
  /** Rich text (HTML) from Summernote. */
  body: string;
}

export interface BenefitContent {
  id: number;
  title: string;
  description: string;
  stat: string;
  icon: string;
  image_url: string | null;
  order: number;
}

export interface PillarContent {
  id: number;
  title: string;
  body: string;
  icon: string;
  order: number;
}

export interface VisionContent {
  background_image_url: string | null;
  slogan: string;
  pillars: PillarContent[];
}

export interface PartnerContent {
  id: number;
  name: string;
  logo_url: string | null;
  group: 'regulator' | 'provider';
  scale: 'sm' | 'md' | 'lg';
  order: number;
}

export interface CtaContent {
  heading: string;
  body: string;
  button_label: string;
  button_link: string;
  image_url: string | null;
}

export interface TickerContent {
  id: number;
  text: string;
  order: number;
}

export interface NavLinkContent {
  id: number;
  label: string;
  href: string;
  order: number;
  children: NavLinkContent[];
}

export interface FooterLinkContent {
  id: number;
  label: string;
  href: string;
  group: 'quick' | 'support';
  order: number;
}

export interface SocialLinkContent {
  id: number;
  platform: string;
  url: string;
  order: number;
}

export interface PageHeaderContent {
  page: string;
  eyebrow: string;
  heading: string;
  body: string;
}

export interface SiteContent {
  hero: HeroContent;
  about: AboutContent;
  benefits: BenefitContent[];
  vision: VisionContent;
  partners: PartnerContent[];
  cta: CtaContent;
  ticker: TickerContent[];
  nav: NavLinkContent[];
  footer_links: FooterLinkContent[];
  socials: SocialLinkContent[];
  page_headers: Record<string, PageHeaderContent>;
}

/**
 * Admin value if it has one, otherwise the copy shipped in the component.
 *
 * Guards against the empty string as well as null/undefined: clearing a field
 * in the admin should restore the built-in wording, not blank the section.
 */
export function pick(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

/** Same idea for lists — an empty list means "nothing configured yet". */
export function pickList<T>(value: T[] | null | undefined, fallback: T[]): T[] {
  return value && value.length > 0 ? value : fallback;
}
